import type { SessionTrace } from '../audio/trace';
import { parseAssembly } from '../composition/lineage';
import type { AssemblyDocument } from '../composition/schema';

const DB_NAME = 'brahm-session-archive-v1';
const STORE = 'sessions';
const MAX_ARCHIVED_SESSIONS = 100;

export interface ArchiveRecord {
  key: string;
  sessionId: string;
  assemblyDigest: string;
  savedAt: number;
  assembly: AssemblyDocument;
  trace?: SessionTrace;
}

export interface ArchivedSessionSummary {
  sessionId: string;
  assemblyDigest: string;
  savedAt: number;
  recordingId: string;
  hasTrace: boolean;
}

export interface ArchiveRepository {
  put(record: ArchiveRecord): Promise<void>;
  get(key: string): Promise<ArchiveRecord | null>;
  list(): Promise<ArchiveRecord[]>;
  prune(maxRows: number): Promise<void>;
  clear(): Promise<void>;
}

export interface ArchiveResult { stored: boolean; reason?: string }

function key(sessionId: string, digest: string): string { return `${sessionId}\u0000${digest}`; }
function reason(error: unknown): string {
  return `Local session archive is unavailable: ${error instanceof Error ? error.message : String(error)}`;
}

class IndexedDbRepository implements ArchiveRepository {
  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB is not supported')); return; }
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'key' });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
    });
  }

  private async transaction<T>(mode: IDBTransactionMode,
    run: (store: IDBObjectStore, resolve: (value: T) => void, reject: (e: unknown) => void) => void,
  ): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
      run(tx.objectStore(STORE), resolve, reject);
      tx.oncomplete = () => db.close();
    });
  }

  async put(record: ArchiveRecord): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(record);
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onabort = () => { db.close(); reject(tx.error ?? new Error('IndexedDB write aborted')); };
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB write failed'));
    });
  }
  async get(recordKey: string): Promise<ArchiveRecord | null> {
    return this.transaction('readonly', (store, resolve, reject) => {
      const request = store.get(recordKey);
      request.onsuccess = () => resolve((request.result as ArchiveRecord | undefined) ?? null);
      request.onerror = () => reject(request.error);
    });
  }
  async list(): Promise<ArchiveRecord[]> {
    return this.transaction('readonly', (store, resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result as ArchiveRecord[]);
      request.onerror = () => reject(request.error);
    });
  }
  async prune(maxRows: number): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const request = store.getAll();
      request.onsuccess = () => {
        const rows = (request.result as ArchiveRecord[]).sort((a, b) => b.savedAt - a.savedAt);
        for (const expired of rows.slice(maxRows)) store.delete(expired.key);
      };
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onabort = () => { db.close(); reject(tx.error ?? new Error('IndexedDB delete aborted')); };
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB delete failed'));
    });
  }
  async clear(): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onabort = () => { db.close(); reject(tx.error ?? new Error('IndexedDB clear aborted')); };
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB clear failed'));
    });
  }
}

export function createSessionArchive(repository: ArchiveRepository = new IndexedDbRepository()) {
  let mutations: Promise<void> = Promise.resolve();
  const exclusive = async <T>(operation: () => Promise<T>): Promise<T> => {
    const previous = mutations;
    let release!: () => void;
    mutations = new Promise<void>(resolve => { release = resolve; });
    await previous;
    try { return await operation(); } finally { release(); }
  };
  const validated = async (row: ArchiveRecord): Promise<ArchiveRecord> => {
    if (!row || row.key !== key(row.sessionId, row.assemblyDigest)) throw new Error('archive key mismatch');
    const assembly = await parseAssembly(row.assembly);
    if (assembly.digest !== row.assemblyDigest) throw new Error('archive assembly identity mismatch');
    return { ...row, assembly };
  };

  return {
    async saveResolvedSession(sessionId: string, value: AssemblyDocument): Promise<ArchiveResult> {
      if (!sessionId) throw new Error('sessionId is required');
      const assembly = await parseAssembly(value);
      const row: ArchiveRecord = { key: key(sessionId, assembly.digest), sessionId,
        assemblyDigest: assembly.digest, savedAt: Date.now(), assembly };
      try {
        await exclusive(async () => {
          await repository.put(row);
          await repository.prune(MAX_ARCHIVED_SESSIONS);
        });
        return { stored: true };
      } catch (error) { return { stored: false, reason: reason(error) }; }
    },

    async finalizeArchivedSession(trace: SessionTrace): Promise<ArchiveResult> {
      const digest = trace.header.assemblyDigest;
      if (typeof digest !== 'string') return { stored: false, reason: 'Trace has no assembly digest' };
      try {
        const found = await exclusive(async () => {
          const record = await repository.get(key(trace.id, digest));
          if (!record) return false;
          const checked = await validated(record);
          await repository.put({ ...checked, trace: structuredClone(trace) });
          return true;
        });
        if (!found) return { stored: false, reason: 'Resolved session was not archived' };
        return { stored: true };
      } catch (error) { return { stored: false, reason: reason(error) }; }
    },

    async listArchivedSessions(): Promise<{
      sessions: ArchivedSessionSummary[]; excludedCorruptRows: number;
    }> {
      const out: ArchivedSessionSummary[] = [];
      let excludedCorruptRows = 0;
      for (const candidate of await repository.list()) {
        try {
          const row = await validated(candidate);
          out.push({ sessionId: row.sessionId, assemblyDigest: row.assemblyDigest,
            savedAt: row.savedAt, recordingId: row.assembly.recordingId, hasTrace: !!row.trace });
        } catch { excludedCorruptRows += 1; }
      }
      return { sessions: out.sort((a, b) => b.savedAt - a.savedAt), excludedCorruptRows };
    },

    async readArchivedSession(sessionId: string, digest: string): Promise<ArchiveRecord | null> {
      try { const row = await repository.get(key(sessionId, digest)); return row ? validated(row) : null; }
      catch { return null; }
    },

    async exportArchive(): Promise<string> {
      const sessions: ArchiveRecord[] = [];
      let excludedCorruptRows = 0;
      for (const row of await repository.list()) {
        try { sessions.push(await validated(row)); } catch { excludedCorruptRows += 1; }
      }
      sessions.sort((a, b) => b.savedAt - a.savedAt);
      return JSON.stringify({ schemaVersion: 1, exportedAt: new Date().toISOString(),
        excludedCorruptRows, sessions }, null, 2);
    },

    async clearArchive(): Promise<void> { try { await repository.clear(); } catch { /* unavailable */ } },
  };
}

const archive = createSessionArchive();
export const saveResolvedSession = archive.saveResolvedSession;
export const finalizeArchivedSession = archive.finalizeArchivedSession;
export const listArchivedSessions = archive.listArchivedSessions;
export const readArchivedSession = archive.readArchivedSession;
export const exportArchive = archive.exportArchive;
export const clearSessionArchive = archive.clearArchive;
